import * as React from "react";
import styled from "styled-components";
import { AdHeader } from "./AdHeader";
import { AdMetrics } from "./AdMetrics";
import { AdInformation } from "./AdInformation";
import { AdSystemInfo } from "./AdSystemInfo";
import useBreaksStore from "../../../../store/breaks";
import { Snackbar, CircularProgress } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import axios from "../../../../services/api";
import { getLocalStorageData } from '../../../../utils/auth';
import { useClipsStore } from "../../../../store/clips";

interface AdDetailsCardProps {
  onUpdateCurrentBreakAndPosition: (datetime: number) => void;
  profile: string;
  name: string;
}

export interface ClipInfo {
  uuid: string;
  duration: number;
}

// -------- helpers ---------------------------------------------------------
const BASE_URL = process.env.REACT_APP_BASE_URL;
const CREATIVE_ROLL_DURATION = Number(
  process.env.REACT_APP_CREATIVE_ROLL_DURATION || 3
);

export const formatTimeToSexagesimal = (seconds: number): string => {
  let [integer, decimal = "000"] = String(seconds).split(".");
  let minutes = Math.floor(Number(integer) / 60);
  let secondsRemaining = Number(integer) % 60;
  return `${String(minutes).padStart(2, "0")}:${String(
    secondsRemaining
  ).padStart(2, "0")}.${decimal}`;
};

export const AdDetailsCard: React.FC<AdDetailsCardProps> = ({
  onUpdateCurrentBreakAndPosition,
  profile,
  name,
}) => {
  const {
    getBreak,
    breaksArray,
    breaks,
    getPosition,
    position,
    setBreak,
    setPosition,
  } = useBreaksStore();

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const clips = useClipsStore((s) => s.clips);
  const currentClipDuration = useClipsStore((s) => s.currentClipDuration);
  const currentClipDatetime = useClipsStore((s) => s.currentClipDatetime);
  const fetchClips = useClipsStore((s) => s.fetchClips);  

  const [currentExhibitionsIndex, setCurrentExhibitionsIndex] =
    React.useState(1);

  const [currentSequence, setCurrentSequence] = React.useState(0);

  const exhibitions = React.useMemo(() => breaksArray || [], [breaksArray]);

  const [inputIndex, setInputIndex] = React.useState("");
  const [lastValidIndex, setLastValidIndex] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState<string>("");
  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleInputChange = (val: string) => {
    setInputIndex(val);
  };

  const showSnack = (message: string) => {
    setSnackMessage(message);
    setSnackOpen(true);
  };

  const goToIndex = (newIndex: number) => {
    // garante limites
    if (newIndex < 0 || newIndex >= exhibitions.length) return;

    // 1. Atualiza break / posição
    setCurrentIndex(newIndex);
    setBreak(exhibitions[newIndex]);
    setPosition(1);

    // 2. Atualiza a timeline (callback)
    const podExhibition = exhibitions[newIndex]?.pod?.pod_exhibitions[0];
    if (podExhibition) {
      const start = new Date(podExhibition.start_datetime).getTime();
      onUpdateCurrentBreakAndPosition(start);
    }

    // 3. Zera sequência interna (caso use)
    setCurrentExhibitionsIndex(0);
    setCurrentSequence(1);
  };

  const handlePodSearch = () => {
    const parsed = parseInt(inputIndex, 10);

    if (!parsed || parsed < 1 || parsed > 9999) {
      setInputIndex(lastValidIndex);
      return;
    }

    const matches = exhibitions.filter((item) => item?.pod?.index === parsed);
    console.log("Matches encontrados:", matches.length);

    if (matches.length === 0) {
      // Nenhum encontrado
      showSnack("Index não encontrado!");
      setInputIndex(lastValidIndex);
      return;
    }

    // Se encontrado, pega o mais recente
    const mostRecent = matches.reduce((a, b) =>
      new Date(a.pod.start_datetime).getTime() >
      new Date(b.pod.start_datetime).getTime()
        ? a
        : b
    );

    const newIndex = exhibitions.findIndex((item) => item.id === mostRecent.id);
    if (newIndex !== -1) {
      goToIndex(newIndex); // ⬅️ exatamente o mesmo efeito do NEXT
      setLastValidIndex(String(parsed));
    }
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const idx = prev < exhibitions.length - 1 ? prev + 1 : prev;
      goToIndex(idx);
      return idx;
    });
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex > 0 ? prevIndex - 1 : prevIndex;
      goToIndex(newIndex);
      return newIndex;
    });
  };

  const handleExhibitions = (index: number) => {
    setCurrentExhibitionsIndex(index);
    setCurrentSequence(index + 1);
    //setPosition(index);
    console.log(getBreak());
    if (getBreak()) {
      const position = getPosition() - 1;
      const podExhibition = getBreak()?.pod.pod_exhibitions[position];
      if (podExhibition) {
        const podExhibitionStartDateTime = new Date(
          podExhibition?.start_datetime
        ).getTime();

        onUpdateCurrentBreakAndPosition(podExhibitionStartDateTime);
      }
    }
  };

  React.useEffect(() => {
    const newIndex = exhibitions[currentIndex]?.pod?.index;
    if (newIndex !== undefined) {
      setInputIndex(String(newIndex));
      setLastValidIndex(String(newIndex)); // se estiver usando lastValidIndex
    }
  }, [currentIndex, exhibitions]);

  React.useEffect(() => {
    const breakItem = getBreak();

    if (!breakItem || !breakItem.id) return;

    const foundIndex = exhibitions.findIndex(
      (item) => item.id === breakItem.id
    );

    if (foundIndex !== -1) {
      setCurrentIndex(foundIndex);
      if (!getPosition()) {
        setPosition(1);
      }
      const podExhibitions =
        exhibitions[foundIndex]?.pod?.pod_exhibitions || [];
      const foundPodIndex = podExhibitions.findIndex(
        (exhibition) => exhibition.position === getPosition()
      );

      if (foundPodIndex !== -1) {
        setCurrentExhibitionsIndex(foundPodIndex);
        setCurrentSequence(foundPodIndex + 1);
        setPosition(foundPodIndex + 1);
      } else {
        setCurrentExhibitionsIndex(0);
        setCurrentSequence(1);
        setPosition(1);
      }
    } else {
      setCurrentIndex(0);
      setCurrentExhibitionsIndex(0);
      setCurrentSequence(1);
      setPosition(1);
    }
  }, [getBreak, breaks, getPosition, exhibitions, position]);

  const downloadFile = async (url: string, filename: string) => {

  const storage = getLocalStorageData();

  let config = {
    method: 'get',
    headers: {
      'Authorization': `Bearer ${storage.token}`
    },
    credentials: 'include'
  } as any;

  try {
    const response = await fetch(url, config)

    if (response.status !== 200) {
      return;
    }

    if (!response.body) return;

    const reader = response.body.getReader();

    let chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      chunks.push(value);
    }
    let blob = new Blob(chunks);

    var file = window.URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = file;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.log(error);
  }
  }

  const downloadFileOld = async (url: string, filename: string) => {
    const response = await axios.get<Blob>(url, {
      responseType: "blob",
      withCredentials: true,
      timeout: 60000,
    });

    const blob = new Blob([response.data]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownload = React.useCallback(
    async (type: "block" | "creative") => {
      try {
        setIsDownloading(true);

        // TODO aqui tive que pegar a info do uuid do clip'dessa forma, se utilizar o useClipsStore da erro de compilação
        // revisar e ver como conseguir a info utilizando o zustand de forma apropriada
        const currentClipState = localStorage.getItem('clips-storage') || '{"state": {"uuid":"undefined"}}';
        const uuid = JSON.parse(currentClipState).state.uuid;
        console.log('Current clip uuid', uuid);
        const pod = exhibitions[currentIndex]?.pod;
        const praca = profile ?? "UNKNOWN";
        const nameSigla = name ?? "UNKNOWN";

        console.log(pod);


        /*const clip = await fetchClips({
          profile: nameSigla,
          datetime: currentClipDatetime,
          duration: currentClipDuration,
        });*/

        if (!pod) throw new Error("Pod não encontrado");
        //if (!clip) throw new Error("Clip não encontrado");
        //if (!currentClipDuration) throw new Error("Duration inválida");

        const startMoment = moment(pod.start_datetime);
        const dataInicio = startMoment.format("DDMMYYYY");
        const horaInicio = startMoment.format("HHmmss");


        const numBloco = String(pod.index).padStart(4, "0");

        let seek: string, to: string, fileName: string, url: string;

        if (type === "block") {
          seek = formatTimeToSexagesimal(0);
          to = formatTimeToSexagesimal(currentClipDuration);
          fileName = `P-${praca}_B-${numBloco}_D-${dataInicio}_H-${horaInicio}.mp4`;
          url = `${BASE_URL}/clips/${uuid}/download`;
        } else {
          const exib = pod.pod_exhibitions[currentExhibitionsIndex];
          if (!exib) throw new Error("Exhibition não encontrado");

          const podStart = moment(pod.start_datetime);
          const adStart = moment(exib.start_datetime);
          const offset =
            CREATIVE_ROLL_DURATION + adStart.diff(podStart, "seconds");
          const creativeDuration = exib.creative.duration;

          seek = formatTimeToSexagesimal(offset - CREATIVE_ROLL_DURATION);
          to = formatTimeToSexagesimal(
            offset + creativeDuration + CREATIVE_ROLL_DURATION
          );
          const indexComercial = String(currentExhibitionsIndex + 1).padStart(
            2,
            "0"
          );
          console.log("comercial seek: ", seek, "to: ", to);
          fileName = `P-${praca}_B-${numBloco}_C-${indexComercial}_D-${dataInicio}_H-${horaInicio}.mp4`;
          url = `${BASE_URL}/clips/${uuid}/trim/${seek}/${to}/download`;
        }
        console.log('filename: ', fileName.replace(/\s+/g, ""), 'type: ', type)

        await downloadFile(url, fileName.replace(/\s+/g, ""));
      } catch (e: any) {
        setSnackMessage(e.message || "Erro no download");
        setSnackOpen(true);
      } finally {
        setIsDownloading(false);
      }
    },
    [exhibitions, currentIndex, currentExhibitionsIndex]
  );

  return (
    <CardWrapper>
      {isDownloading && (
        <Overlay>
          <CircularProgress />
        </Overlay>
      )}
      {exhibitions.length > 0 ? (
        <CardContainer key={exhibitions[currentIndex]?.id}>
          <AdHeader
            key={exhibitions[currentIndex]?.pod.id}
            id={String(exhibitions[currentIndex]?.pod.index)}
            sequence={currentSequence}
            onNext={handleNext}
            onPrev={handlePrev}
            exhibitions={exhibitions[currentIndex]?.pod.pod_exhibitions.length}
            onExhibitions={handleExhibitions}
            inputIndex={inputIndex}
            onInputIndexChange={handleInputChange}
            onCommitIndex={handlePodSearch}
          />
          <AdMetrics
            key={exhibitions[currentIndex]?.pod?.index}
            duration={
              (new Date(exhibitions[currentIndex]?.pod.end_datetime).getTime() -
                new Date(
                  exhibitions[currentIndex]?.pod.start_datetime
                ).getTime()) /
              1000
            }
            slate={exhibitions[currentIndex]?.slate?.duration}
            onDownload={() => {handleDownload("block");}}
          />

          <>
            <AdInformation
              key={`${exhibitions[currentIndex]?.id}-${exhibitions[currentIndex]?.pod?.pod_exhibitions[currentExhibitionsIndex]?.position}`}
              adId={String(
                exhibitions[currentIndex]?.pod.pod_exhibitions[
                  currentExhibitionsIndex
                ]?.creative?.id ?? ""
              )}
              title={
                exhibitions[currentIndex]?.pod.pod_exhibitions[
                  currentExhibitionsIndex
                ]?.creative?.title ?? "Sem título"
              }
              description={
                exhibitions[currentIndex]?.pod.pod_exhibitions[
                  currentExhibitionsIndex
                ]?.creative?.description ?? "Sem descrição"
              }
              adDuration={
                Math.max(
                  1,
                  exhibitions[currentIndex]?.pod?.pod_exhibitions[
                    currentExhibitionsIndex
                  ]?.creative?.duration
                ) ?? 0
              }
              onDownload={() => handleDownload("creative")}
            />
            <AdSystemInfo
              key={`${exhibitions[currentIndex]?.id}`}
              systemId={
                exhibitions[currentIndex]?.pod.pod_exhibitions[
                  currentExhibitionsIndex
                ]?.vast_id ?? ""
              }
              systemName={
                exhibitions[currentIndex]?.pod.pod_exhibitions[
                  currentExhibitionsIndex
                ]?.vast_sytem ?? ""
              }
              streamId={
                exhibitions[currentIndex]?.pod.pod_exhibitions[
                  currentExhibitionsIndex
                ]?.stream_id ?? ""
              }
            />
          </>
        </CardContainer>
      ) : (
        <LoadingMessage>Carregando informações</LoadingMessage>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="warning"
          variant="filled"
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  border-radius: 0;
  display: flex;
  /*min-width: 580px;
  width: 580px;*/
  min-width: 580px;
  max-width: 580px;
  width: 100%;
  flex-direction: column;
  height: "auto";
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
`;

const CardContainer = styled.div`
  border-radius: 8px;
  background-color: rgba(243, 243, 243, 1);
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 10px;
  font-family: "Roboto", "Montserrat";

  @media (max-width: 991px) {
    max-width: 100%;
  }
`;

const LoadingMessage = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  height: 560px;
  font-family: "Roboto", "Montserrat";
  font-size: 24px;

  @media (max-width: 991px) {
    max-width: 100%;
  }
`;
